/** @module application */

/**
 * Tracks whether certain elements are 'in view' and may update the hash of the URL or marking certain elements with a class <code>is-active</code> accordingly.
 * An element is 'in view' if it is scrolled passed an viewport offset defined by parameter {@link options.viewportMargin}. If this parameter is omitted, an element is considered 'in view', if it overlaps the center of the screen by at least 1px.
 * If two or more elements are 'in view' in the same time, the last encountered in the DOM 'wins'.
 *
 * @param {object} [options] - Initial options.
 * @param {string} [options.attrName='data-section-nav-anchor'] - The name of the attribute for selecting elements to watch and eventually get the new hash from. If the value of this attribute is empty, the element's id-attribute is used instead.
 * @param {string} [options.stateSelector='.js-section-nav-state-link'] - The selector to get elements that should be marked as 'active' (= adding class <code>is-active</code>) if matching elements having an attribute defined by {@link options.attrName} are 'in view'. The elements are considered 'active' if they match the selector fetched from {@link options.getActiveStateSelector} when passing in the hash received from {@link options.getHashFromElements}, or, if that property is omitted, the current hash matches their href-attribute.
 * @param {string} [options.viewportMargin='-50%'] - CSS-Margin string to apply to the viewport for detecting when an element is considered 'in view'.  Defaults to <code>'-50%'</code>, meaning that an element must reach the center of the screen to count as 'in view'.
 * @param {boolean} [options.updateHash=false] - Whether manually scrolling should update the hash of the URL with the value fetched by {@link options.getHashFromElement}.
 * @param {boolean} [options.updateStateLinks=true] - Whether state-elements should be updated based on elements 'in-view'. See {@link options.stateSelector} and {@link options.getActiveStateSelector}.
 * @param {function(HTMLElement):string} [options.getHashFromElement] - An optional function returning the hash to set to the url or matching the elements received from {@link options.stateSelector} against. Receives the element currently 'in view'. If omitted, the hash is taken from the attribute defined by {@link options.attrName}, or, if that attribute's value is empty, from the id-attribute from the element that is vurrently 'in-view'.
 * @param {function(string):string} [options.getActiveStateSelector] - An optional function returning the selector for the active element defined by {@link options.stateSelector}. Receives the current hash as fetched from {@link options.getHashFromElement}. If omitted, <code>`[href="${hash}"]`</code> is used.
 * @param {function(HTMLElement, string)} [options.onEnter] - An optional callback function, that is called when an element comes 'in view', but after the hash and states are updated, if applicable. Receives the current element and the hash.
 */
export class SectionNav {
    constructor(options = {}) {
        this.defaultOptions = {
            attrName: 'data-section-nav-anchor',
            stateSelector: '.js-section-nav-state-link',
            viewportMargin: '-50%',
            updateHash: false,
            updateStateLinks: true,
            getHashFromElement: undefined,
            getActiveStateSelector: undefined,
            onEnter: undefined,
        };

        this.options = { ...this.defaultOptions, ...options };

        this._init();
    }

    _init() {
        const options = this.options,
            attrSelector = `[${options.attrName}]`;

        let observer = new IntersectionObserver(this._onIntersecting.bind(this), {
            rootMargin: options.viewportMargin,
        });

        document.querySelectorAll(`${attrSelector}`).forEach((node) => {
            observer.observe(node);
        });
    }

    _onIntersecting(entries) {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                let el = entry.target,
                    options = this.options,
                    activeClass = 'is-active',
                    hash;

                if (typeof options.getHashFromElement === 'function') {
                    hash = options.getHashFromElement(el);
                } else {
                    hash = el.getAttribute(options.attrName) || el.id;
                }

                if (options.updateHash === true) {
                    if (location.hash.indexOf('#' + hash) === -1) {
                        history.replaceState({ state: hash }, null, '#' + hash);
                    }
                }

                if (options.updateStateLinks) {
                    let activeStateSelector;

                    if (typeof options.getActiveStateSelector === 'function') {
                        activeStateSelector = options.getActiveStateSelector(hash);
                    } else {
                        activeStateSelector = `[href="#${hash}"]`;
                    }

                    document.querySelectorAll(options.stateSelector).forEach((link) => {
                        if (link.matches(activeStateSelector)) {
                            link.parentElement.classList.add(activeClass);
                        } else {
                            link.parentElement.classList.remove(activeClass);
                        }
                    });
                }

                if (typeof options.onEnter === 'function') {
                    options.onEnter(el, hash);
                }
            }
        });
    }
}
